type SiteInformation = {
	tenant?: { shard?: unknown } | null;
	shard?: unknown;
};

type SiteInformationValue = SiteInformation | Promise<SiteInformation | null> | null;

type WindowWithSiteInformation = Window & {
	__SITEINFORMATION__?: SiteInformationValue;
	__SITEINFORMATION_PROMISE__?: SiteInformationValue;
};

let cachedShard: string | null | undefined;
let currentLookup: Promise<string | undefined> | undefined;

const normalizeShard = (shard: unknown): string | undefined =>
	typeof shard === 'string' ? shard.trim() || undefined : undefined;

const readJiraShard = (): string | undefined =>
	normalizeShard(window.document.querySelector('meta[name="ajs-shard"]')?.getAttribute('content'));

const readSiteInformationShard = async (
	value: SiteInformationValue | undefined,
): Promise<string | undefined> => {
	let timeout: ReturnType<typeof setTimeout> | undefined;
	try {
		const data = await Promise.race([
			value,
			new Promise<undefined>((resolve) => {
				timeout = setTimeout(() => resolve(undefined), 5000);
			}),
		]);
		return normalizeShard(data?.tenant?.shard ?? data?.shard);
	} catch {
		// Tenant metadata is best effort and must never break instrumentation.
		return undefined;
	} finally {
		clearTimeout(timeout);
	}
};

const readConfluenceShard = async (): Promise<string | undefined> => {
	try {
		const browser = window as WindowWithSiteInformation;
		const siteShard = readSiteInformationShard(browser.__SITEINFORMATION__);
		const sessionShard = readSiteInformationShard(browser.__SITEINFORMATION_PROMISE__);
		return (await siteShard) ?? (await sessionShard);
	} catch {
		return undefined;
	}
};

const readCurrentTenantShard = async (): Promise<string | undefined> => {
	try {
		if (typeof window === 'undefined') {
			return undefined;
		}

		return readJiraShard() ?? readConfluenceShard();
	} catch {
		return undefined;
	}
};

/** Reads the current host page's shard without fetching or requiring product context. */
export const getCurrentTenantShard = (): Promise<string | undefined> => {
	if (typeof window === 'undefined') {
		return Promise.resolve(undefined);
	}
	if (cachedShard !== undefined) {
		return Promise.resolve(cachedShard ?? undefined);
	}
	if (currentLookup) {
		return currentLookup;
	}

	currentLookup = readCurrentTenantShard()
		.then((shard) => {
			cachedShard = shard ?? null;
			return shard;
		})
		.finally(() => {
			currentLookup = undefined;
		});
	return currentLookup;
};
